import React, { useEffect, useState, useRef } from 'react';
import { AudioWaveform } from './AudioWaveform';
import { MessageSquare, Phone } from 'lucide-react';
interface TranscriptionPaneProps {
  transcription: Array<{
    text: string;
    type: 'phone' | 'sms';
    sender?: 'customer' | 'csr';
  }>;
  mode?: 'sms' | 'phone' | 'mixed';
}
export const TranscriptionPane: React.FC<TranscriptionPaneProps> = ({
  transcription,
  mode = 'phone'
}) => {
  const transcriptionEndRef = useRef<HTMLDivElement>(null);
  const [heights, setHeights] = useState(() => Array.from({
    length: 4
  }, () => Math.floor(Math.random() * 12) + 4));
  useEffect(() => {
    const interval = setInterval(() => {
      setHeights(Array.from({
        length: 4
      }, () => Math.floor(Math.random() * 12) + 4));
    }, 150);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (transcriptionEndRef.current) {
      transcriptionEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [transcription]);
  return <div className="bg-card rounded-lg shadow-md h-[200px] md:h-[calc(100vh-8rem)] flex flex-col" data-id="element-170">
      <div className="sticky top-0 z-40 bg-card/80 backdrop-blur-sm p-4 md:p-6 border-b" data-id="element-171">
        <div className="flex justify-between items-center mb-3 md:mb-4" data-id="element-172">
          <div className="flex items-center gap-2" data-id="element-173">
            <h2 className="text-lg md:text-xl font-semibold" data-id="element-174">
              Live Transcription
            </h2>
            <div className="flex items-center gap-2" data-id="element-175">
              <div className="flex items-end gap-[2px] h-4 pt-1" data-id="element-176">
                {heights.map((height, i) => <div key={i} className="w-[3px] bg-red-500 rounded-full transition-all duration-150" style={{
                height: `${height}px`
              }} data-id="element-177" />)}
              </div>
              <span className="text-xs text-muted-foreground" data-id="element-178">LIVE</span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-179">
            {mode === 'mixed' ? <>
                <div className="hidden md:flex items-center gap-2 bg-secondary px-3 py-1 rounded-full mr-2" data-id="element-180">
                  <Phone className="w-4 h-4" data-id="element-181" />
                  <span className="text-sm font-medium" data-id="element-182">Phone</span>
                </div>
                <div className="hidden md:flex items-center gap-2 bg-secondary px-3 py-1 rounded-full" data-id="element-183">
                  <MessageSquare className="w-4 h-4" data-id="element-184" />
                  <span className="text-sm font-medium" data-id="element-185">SMS</span>
                </div>
                <div className="flex md:hidden items-center gap-1" data-id="element-186">
                  <Phone className="w-4 h-4" data-id="element-187" />
                  <MessageSquare className="w-4 h-4" data-id="element-188" />
                </div>
              </> : <div className="flex items-center gap-2 bg-secondary px-3 py-1 rounded-full" data-id="element-189">
                {mode === 'phone' ? <Phone className="w-4 h-4" data-id="element-190" /> : <MessageSquare className="w-4 h-4" data-id="element-191" />}
                <span className="hidden md:inline text-sm font-medium capitalize" data-id="element-192">
                  {mode}
                </span>
              </div>}
          </div>
        </div>
        {mode === 'phone' && <div className="mb-3 md:mb-4" data-id="element-193">
            <AudioWaveform data-id="element-194" />
          </div>}
      </div>
      <div className="flex-1 overflow-y-auto bg-background p-3 md:p-4 rounded-md" data-id="element-195">
        <div className="space-y-2 md:space-y-3" data-id="element-196">
          {transcription.map((line, index) => {
          const isCustomer = line.sender === 'customer' || line.text.startsWith('Customer:');
          const isFirstOrDifferentType = index === 0 || transcription[index - 1]?.type !== line.type || transcription[index - 1]?.sender !== line.sender;
          if (line.type === 'phone') {
            return <div key={index} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`} data-id="element-197">
                  <div className="flex items-start gap-2 max-w-[85%] md:max-w-[80%]" data-id="element-198">
                    {isCustomer && <Phone className="w-3 h-3 mt-1 text-muted-foreground" data-id="element-199" />}
                    <div className="flex flex-col" data-id="element-200">
                      {!isCustomer && isFirstOrDifferentType && <div className="text-xs text-muted-foreground mb-1 text-right" data-id="element-201">
                          CSR
                        </div>}
                      <div className={`${isCustomer ? 'border-l-4 border-primary pl-3' : 'text-muted-foreground'}`} data-id="element-202">
                        <p className={`text-sm md:text-base ${isCustomer ? 'font-medium' : ''}`} data-id="element-203">
                          {line.text.replace(/(Customer:|CSR:)\s/, '')}
                        </p>
                      </div>
                    </div>
                    {!isCustomer && <Phone className="w-3 h-3 mt-1 text-muted-foreground" data-id="element-204" />}
                  </div>
                </div>;
          } else {
            return <div key={index} className={`mb-3 flex ${!isCustomer ? 'justify-end' : 'justify-start'}`} data-id="element-205">
                  <div className="flex items-start gap-2 max-w-[80%]" data-id="element-206">
                    {isCustomer && <MessageSquare className="w-3 h-3 mt-2 text-muted-foreground" data-id="element-207" />}
                    <div className="flex flex-col" data-id="element-208">
                      {!isCustomer && isFirstOrDifferentType && <div className="text-xs text-muted-foreground mb-1 text-right" data-id="element-209">
                          CSR
                        </div>}
                      <div className={`rounded-lg px-4 py-2 ${isCustomer ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`} data-id="element-210">
                        <p className="text-sm whitespace-pre-line" data-id="element-211">
                          {line.text.replace(/(Customer:|CSR:)\s/, '')}
                        </p>
                      </div>
                    </div>
                    {!isCustomer && <MessageSquare className="w-3 h-3 mt-2 text-muted-foreground" data-id="element-212" />}
                  </div>
                </div>;
          }
        })}
        </div>
        <div ref={transcriptionEndRef} data-id="element-213" />
      </div>
    </div>;
};